<template>
  <v-expansion-panel>
    <solicitante-dialog-tabs
      :is-configurar-solicitante-dialog-open="isConfigurarSolicitanteDialogOpen"
      :solicitante-id="solicitanteId"
      :close-dialog="closeDialog"
      @refetch-data="$emit('refetch-data')"
    />

    <v-expansion-panel-header>
      <h3 class="py-2 font-weight-bold">
        Dados do solicitante
      </h3>
    </v-expansion-panel-header>

    <v-expansion-panel-content>
      <v-form class="multi-col-validation">
        <v-row>
          <v-col
            cols="12"
            sm="10"
          >
            <v-text-field
              v-model.trim="solicitanteDataLocale.nome"
              dense
              outlined
              label="Nome"
              validate-on-blur
              hint="Máximo de 150 caracteres"
              :disabled="inativarInputs"
              :filled="inativarInputs"
              :rules="[validators.required, v => (!!v && v.length <= 150) || 'Máximo de 150 caracteres']"
              @input="enviarDados"
            />
          </v-col>

          <v-col
            cols="12"
            sm="2"
          >
            <v-btn
              outlined
              :disabled="inativarBotao"
              @click="selecionarSolicitante"
            >
              <v-icon>{{ icons.mdiAccountEditOutline }}</v-icon>
            </v-btn>
          </v-col>

          <v-col
            cols="12"
            sm="8"
          >
            <v-text-field
              :value="solicitanteEmail"
              dense
              outlined
              label="Email"
              validate-on-blur
              hint="Máximo de 150 caracteres"
              :disabled="inativarInputs"
              :filled="inativarInputs"
              :rules="[validators.required, v => (!!v && v.length <= 150) || 'Máximo de 150 caracteres']"
              @change="enviarDados"
            />
          </v-col>

          <v-col
            cols="12"
            sm="4"
          >
            <v-text-field
              v-mask="'(##) #####-####'"
              :value="solicitanteCelular"
              dense
              outlined
              label="Celular"
              validate-on-blur
              :disabled="inativarInputs"
              :filled="inativarInputs"
              :rules="[validators.required]"
              @change="enviarDados"
            />
          </v-col>

          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-if="solicitanteDataLocale.cpf"
              :value="maskCpf(solicitanteDataLocale.cpf)"
              dense
              outlined
              label="CPF"
              :disabled="inativarInputs"
              :filled="inativarInputs"
              readonly
            />
          </v-col>

          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model.trim="solicitanteDataLocale.orgaoEmissor"
              dense
              outlined
              validate-on-blur
              label="Órgão emissor:"
              :disabled="inativarInputs"
              :filled="inativarInputs"
              :rules="[v => (!!v && v.length <= 20) || 'Máximo de 20 caracteres']"
              @change="enviarDados"
            />
          </v-col>

          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model.trim="dataNascimentoLocale"
              v-mask="'##/##/####'"
              dense
              outlined
              validate-on-blur
              label="Data de nascimento"
              :disabled="inativarInputs"
              :filled="inativarInputs"
              :rules="[v => !v || v.length === 10 || 'Formato não válido']"
              @change="enviarNovaData"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { required } from '@core/utils/validation'
import { mdiAccountEditOutline } from '@mdi/js'
import { maskCpf } from '@/utils/validation'
import SolicitanteDialogTabs from '@/views/solicitante/SolicitanteDialogTabs.vue'
import SolicitanteEnderecoModel from '@/models/entities/solicitante/SolicitanteEnderecoModel'
import SolicitanteModel from '@/models/entities/solicitante/SolicitanteModel'
import useDadosCorreios from '@/composables/useDadosCorreios'
import { converterLocaleDateStringParaOFormatoIsoDateString, converterDateStringEmLocaleDateString } from '@/utils/dateTime'

export default {
  emits: ['alterar-dados-solicitante'],

  components: {
    SolicitanteDialogTabs,
  },

  props: {
    editavel: {
      type: Boolean,
      required: true,
    },
    ticketEstaFinalizado: {
      type: Boolean,
      required: true,
    },
    solicitanteData: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const isImprimirSecaoDialogOpen = ref(false)

    const solicitanteDataLocale = ref(new SolicitanteModel())
    const { enderecoData } = useDadosCorreios()

    watch(
      () => props.solicitanteData,
      value => {
        solicitanteDataLocale.value = value
        enderecoData.value = value.endereco || new SolicitanteEnderecoModel()
        delete enderecoData.value.id
      },
      { immediate: true, deep: true },
    )

    const dataNascimentoLocale = ref(
      solicitanteDataLocale.value.dataNascimento ? converterDateStringEmLocaleDateString(solicitanteDataLocale.value.dataNascimento) : null,
    )

    function enviarDados() {
      emit('alterar-dados-solicitante', { ...solicitanteDataLocale.value, endereco: enderecoData.value })
    }
    function enviarNovaData(novaData) {
      emit('alterar-dados-solicitante', {
        ...solicitanteDataLocale.value,
        endereco: enderecoData.value,
        dataNascimento: converterLocaleDateStringParaOFormatoIsoDateString(novaData),
      })
    }

    const inativarInputs = ref(true)
    const inativarBotao = computed(() => !props.editavel || props.ticketEstaFinalizado)

    const isConfigurarSolicitanteDialogOpen = ref(false)
    const solicitanteId = ref(0)

    function selecionarSolicitante() {
      solicitanteId.value = solicitanteDataLocale.value.id
      isConfigurarSolicitanteDialogOpen.value = true
    }

    function closeDialog() {
      isConfigurarSolicitanteDialogOpen.value = false
      solicitanteId.value = 0
    }

    const solicitanteFromTicket = ref(true)

    const solicitanteEmail = computed(() => solicitanteDataLocale.value.contatos[0]?.email)
    const solicitanteCelular = computed(() => solicitanteDataLocale.value.contatos[0]?.celular)

    return {
      isImprimirSecaoDialogOpen,
      solicitanteDataLocale,
      enderecoData,
      dataNascimentoLocale,
      isConfigurarSolicitanteDialogOpen,
      solicitanteId,
      solicitanteFromTicket,
      selecionarSolicitante,
      closeDialog,
      enviarDados,
      enviarNovaData,
      inativarInputs,
      inativarBotao,
      solicitanteEmail,
      solicitanteCelular,
      maskCpf,
      validators: { required },
      icons: { mdiAccountEditOutline },
    }
  },
}
</script>
